/**
 * bucketLabels — the 4 canonical Compare bucket labels, with their BCT variants.
 *
 * Extracted verbatim from `CompareToThriveModal` so the Add panel
 * (`ExportToThrivePanel`) renders the SAME sentences for the same numbers. Two
 * surfaces describing one dry run in two vocabularies is the defect this move
 * exists to prevent — the panel is the commit step for the preview the modal
 * shows, and a recruiter reading "added to the search" in one and something
 * else in the other has no way to tell whether they are looking at the same
 * run.
 *
 * Pure: no React, no I/O. The BCT rationale comments are load-bearing and
 * travelled with the code.
 */

import type { CompareBuckets } from "@nu-assist/contracts";

export function getBucketLabels(
  isBct: boolean
): { key: keyof CompareBuckets; label: (n: number) => string }[] {
  return [
    {
      key: "new",
      label: isBct
        ? (n) => `${n} new contacts. Created in Thrive, not added to a search.`
        : (n) => `${n} new contacts. Created in Thrive and added to this search.`,
    },
    {
      key: "existing_add_to_search",
      // Both BCT rows end in "no change" because for a Company Map neither
      // one changes anything — the distinction between them is search
      // MEMBERSHIP, which a Company Map write does not touch. They must still
      // be told apart: an earlier revision of this had both buckets reading
      // "already in Thrive — …", two adjacent lines with different counts and
      // nothing explaining the split (/pr-review iter 2, A 2g + C Imp 2).
      // Naming the axis is what makes the two counts legible.
      label: isBct
        ? (n) => `${n} in Thrive, outside the original search. No change.`
        : (n) => `${n} in Thrive but not in this search. Added to the search.`,
    },
    {
      key: "linkedin_position_update" as const,
      label: (n: number) =>
        `${n} LinkedIn-URL matches. Job position updated, other Thrive fields left untouched.`,
    },
    {
      key: "already_in_search" as const,
      // The BCT variant is not cosmetic: a Company Map's contacts are never
      // added to a search, so "already in this search" states something that
      // cannot be true of the rows it is counting. Same defect class as the
      // `new` and `existing_add_to_search` variants above — it was missed
      // because the bucket KEY says "search" and the copy was read as
      // matching the key rather than the data (/pr-review iter 1, A 2a/3a).
      label: isBct
        ? (n: number) => `${n} in Thrive, inside the original search. No change.`
        : (n: number) => `${n} already in this search. No change.`,
    },
  ];
}
